import React, { useState, useEffect, useRef } from 'react';
import { authFetch } from '../../services/authFetch';
import AIHelpPanel from './AIHelpPanel';
import MarkdownRenderer from './MarkdownRenderer';
import ContentFigure from './ContentFigure';
import TranslateAmharicButton from './TranslateAmharicButton';
import CopyLinkButton from '../CopyLinkButton';
import FeedbackPrompt from '../feedback/FeedbackPrompt';
import ShareButton from '../ShareButton';
import {
  captureShareCardNode,
  downloadBundle,
  nativeShareBundle,
} from '../ShareCard';
import ShareCardRender from '../ShareCardRender';
import MediaDisplay, { MediaItem } from './MediaDisplay';
// CoachCard hidden for launch
// import CoachCard, { CoachAction } from '../CoachCard/CoachCard';
import { aiTutorAPI, pastExamAPI, ComponentTrigger, track } from '../../services/api';
import { displayTopic } from '../../config/topicDisplayNames';
import PassagePanel from '../PassagePanel';
import { Reportable } from '../Reportable/Reportable';
import './PastESSLCEQuiz.css';

interface ExamQuestion {
  question_id: string;
  /** Set by the spaced-review blend — a re-served past miss. */
  is_review?: boolean;
  /** Opaque 8-char base62 slug, preferred for share URLs. */
  public_slug?: string;
  question_text: string;
  /** The passage, rules or scenario this question shares with its group. */
  passage?: string | null;
  question_number: number;
  has_media?: boolean;
  media?: MediaItem[];
  /** An inline SVG chart, for authored items whose question IS the figure.
   *  Past papers carry diagrams as hosted `media`; a generated data-
   *  interpretation item has no asset host, so its chart rides with it. */
  figure?: { svg: string; alt?: string };
  exam: {
    subject: string;
    year: number;
    year_ec?: number;
    exam_type: string;
  };
  metadata: {
    topic: string;
    subtopic: string;
    difficulty_level: string;
    correct_answer: string;
    explanation?: string;
  };
  options: Array<{
    label: string;
    text: string;
    is_correct: boolean | null;
  }>;
}

/** Where a subject's last-chosen section is remembered. Per subject, because
 *  ESLCE Geography and UAT are different papers with different sections. */
const SECTION_KEY = (subject: string) => `lomi_section:${subject}`;

const rememberSection = (subject: string, section: string) => {
  try {
    if (section) localStorage.setItem(SECTION_KEY(subject), section);
    else localStorage.removeItem(SECTION_KEY(subject));
  } catch {
    /* storage unavailable — the filter still works, it just will not persist */
  }
};

interface FilterOptions {
  years: number[];
  topics: Array<{
    name: string;
    count: number;
    pct: number;
    section?: string;
    /** Server-derived display name that fits a chip. Authored topic names run
     *  to 44 characters, which wraps a filter row onto three lines. */
    short?: string;
  }>;
  categories?: Array<{ name: string; count: number }>;
}

interface TopicStat {
  topic: string;
  questions_answered: number;
  correct: number;
  accuracy: number;
  total_available: number;
}

interface PastExamStats {
  total_questions_practiced: number;
  total_correct: number;
  accuracy_percent: number;
  total_sessions: number;
  topics?: TopicStat[];
  suggested_topic?: string | null;
}

interface PastESSLCEQuizProps {
  subject: string;
  /**
   * What to call the source of an item. ESSLCE items ARE past ESLCE papers and
   * the badge earns its place; UAT items are authored practice, and stamping
   * "ESLCE '24 EC" on them would claim provenance they do not have. With no
   * textbook to check us against, that credibility is the scarce thing —
   * so packages that are not ESSLCE pass their own label, or none.
   */
  sourceLabel?: string | null;
  topic?: string;
  subtopic?: string;
  studentId?: string;
}

const PastESSLCEQuiz: React.FC<PastESSLCEQuizProps> = ({ subject, sourceLabel, topic: initialTopic, subtopic, studentId }) => {
  const [questions, setQuestions] = useState<ExamQuestion[]>([]);
  const [totalInPool, setTotalInPool] = useState<number>(0);
  const [unseenRemaining, setUnseenRemaining] = useState<number>(0);
  // Fresh questions left across the WHOLE subject (ignores the topic filter).
  // Lets us steer an exhausted-topic student to untouched topics instead of
  // wrongly telling them they've finished the entire subject.
  const [subjectUnseenRemaining, setSubjectUnseenRemaining] = useState<number>(0);
  const batchNumberRef = useRef<number>(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [answers, setAnswers] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAIHelp, setShowAIHelp] = useState(false);
  const [explanationExpanded, setExplanationExpanded] = useState(false);

  // Filter state
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({ years: [], topics: [] });
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedTopic, setSelectedTopic] = useState<string>(initialTopic || '');
  // The paper's section — Verbal / Quantitative for UAT. A different axis from
  // topic, and empty for subjects that do not use it, so the row disappears.
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  /** The free-tier verdict from the server, present only for a paid package. */
  const [allowance, setAllowance] = useState<
    { access: string; limit: number | null; used: number; remaining: number | null } | null
  >(null);
  /** How many questions this filter holds that the student cannot open. */
  const [lockedCount, setLockedCount] = useState(0);
  /** Topics holding at least one question this student may open. */
  const [freeTopics, setFreeTopics] = useState<string[] | null>(null);
  /**
   * The free tier is used up (or the day ran out). Everything shuts.
   *
   * NOT the same as the package having no questions yet — `remaining <= 0` is
   * true in both, and this wall says "used up", which is an accusation rather
   * than a description when the shelf is simply empty. The server marks that
   * case, because it is the only side that can tell the two apart.
   */
  const poolEmpty = !!(allowance as any)?.empty;
  const trialSpent =
    !!allowance &&
    allowance.access !== 'full' &&
    !poolEmpty &&
    (allowance.remaining ?? 1) <= 0;
  /**
   * On the trial at all, spent or not.
   *
   * "All" is disabled for the whole trial, not just at the end of it. The free
   * questions live in three topics, so "All" promises the entire bank and
   * hands back twenty — and it is the DEFAULT, so a trial student's first
   * screen was the one filter that misrepresents what they have. Choosing one
   * of the three open topics is the honest starting point.
   */
  const onTrial = !!allowance && allowance.access !== 'full';

  // Sync topic prop changes (e.g. when coach card switches to practice tab with a topic)
  useEffect(() => {
    if (initialTopic) {
      setSelectedTopic(initialTopic);
    }
  }, [initialTopic]);

  // With "All" disabled for the trial, the default filter is a button the
  // student cannot press — so land them on a topic that is actually open
  // instead of on an empty selection.
  //
  // The free topic IN THE CURRENT SECTION, never simply the first one. The
  // invariant below clears a topic that does not belong to the selected
  // section, so picking across sections would set it and have it cleared on
  // the next render, forever. There is one free topic per section, so once
  // the filter options have loaded a match always exists.
  useEffect(() => {
    if (!onTrial || trialSpent || selectedTopic) return;
    if (!freeTopics || !freeTopics.length || !filterOptions.topics.length) return;
    const sectionOf = (name: string) =>
      filterOptions.topics.find((t) => t.name === name)?.section;
    const pick = selectedCategory
      ? freeTopics.find((t) => sectionOf(t) === selectedCategory)
      : freeTopics[0];
    if (pick) setSelectedTopic(pick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onTrial, trialSpent, freeTopics, selectedCategory, filterOptions.topics]);

  // A topic belongs to exactly one section, so changing section almost always
  // invalidates the chosen topic. Leaving it set asked the server for
  // "Verbal Reasoning + Geometry", got nothing back, and told the student
  // "No questions found for this filter" — for a combination they never chose
  // and could not see, because the topic row only draws chips from the CURRENT
  // section, so the stale one was selected and invisible at the same time.
  //
  // Written as an invariant rather than inside the chip's onClick because the
  // section also changes on load, from the remembered one.
  useEffect(() => {
    if (!selectedCategory || !selectedTopic) return;
    const known = filterOptions.topics.find((t) => t.name === selectedTopic);
    // Still loading, or a topic with no section of its own: leave it alone.
    if (!known || !known.section) return;
    if (known.section !== selectedCategory) setSelectedTopic('');
  }, [selectedCategory, selectedTopic, filterOptions.topics]);

  // Pagination for question grid (5 per page)
  const QUESTIONS_PER_PAGE = 5;
  const [gridPage, setGridPage] = useState(0);

  // Session tracking for AI context
  const [timeOnQuestion, setTimeOnQuestion] = useState(0);
  const [consecutiveWrong, setConsecutiveWrong] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const shareCardRef = useRef<HTMLDivElement | null>(null);

  // Component triggers for follow-up prompts
  const [resultTriggers, setResultTriggers] = useState<ComponentTrigger[]>([]);

  // Initial request for AI panel (when trigger is clicked)
  const [triggerPrompt, setTriggerPrompt] = useState<string | null>(null);

  // Persistence state
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [stats, setStats] = useState<PastExamStats | null>(null);
  const [resumePrompt, setResumePrompt] = useState(false);
  const [activeSessionData, setActiveSessionData] = useState<any>(null);
  const [topicStatsExpanded, setTopicStatsExpanded] = useState(false);
  const skipSessionStart = useRef(false);

  // A practice run = ONE session that can span multiple batches (see
  // handleRestart). The combo (consecutive-correct streak) is computed per
  // session_id, so we keep the same session alive while the student taps
  // "Practice another", and only mark it completed when the run actually ends
  // (leaving, switching filters/subject) — that's when the combo banks to the
  // leaderboard. These refs let leave/unmount handlers read the latest values
  // without re-subscribing.
  const sessionIdRef = useRef<string | null>(null);
  const studentIdRef = useRef<string | undefined>(studentId);
  const subjectRef = useRef<string>(subject);
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);
  useEffect(() => { studentIdRef.current = studentId; }, [studentId]);
  useEffect(() => { subjectRef.current = subject; }, [subject]);

  // Complete the current run (if any) so its combo banks. Idempotent: nulls the
  // ref first so a concurrent caller (e.g. filter-change + unmount) can't
  // double-complete or race the next session's start.
  const completeCurrentRun = async () => {
    const sid = sessionIdRef.current;
    const stid = studentIdRef.current;
    if (!sid || !stid) return;
    sessionIdRef.current = null;
    try {
      await pastExamAPI.completeSession(sid, stid);
      track('past_exam.run.completed', { subject: subjectRef.current });
    } catch (err) {
      console.error('Failed to complete run:', err);
    }
  };

  // Leaving the quiz ends the run — complete it so the combo banks.
  useEffect(() => {
    return () => { completeCurrentRun(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check for active session and load stats on mount
  useEffect(() => {
    if (!studentId) return;
    const checkSession = async () => {
      try {
        const [sessionData, statsData] = await Promise.all([
          pastExamAPI.getActiveSession(studentId, subject),
          pastExamAPI.getStats(studentId, subject)
        ]);
        if (sessionData.active_session && (sessionData.active_session.responses?.length || 0) > 0) {
          // Only show resume prompt if the session has at least 1 answered question.
          // A session with 0 responses is essentially a fresh start — no need to prompt.
          const startedAt = new Date(sessionData.active_session.started_at);
          const hoursOld = (Date.now() - startedAt.getTime()) / (1000 * 60 * 60);
          setActiveSessionData({ ...sessionData.active_session, hoursOld });
          setResumePrompt(true);
          skipSessionStart.current = true;
        }
        setStats(statsData);
      } catch (err) {
        console.error('Failed to check active session:', err);
      }
    };
    checkSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, subject]);

  // Fetch filter options on mount or subject change
  useEffect(() => {
    loadFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject]);

  // Load questions when filters change. Reset batch counter so the next
  // load is recorded as batch #1 of a fresh filter, not a continuation.
  // A filter/subject change ends the current run (different question set), so
  // complete it first to bank its combo before the new session starts.
  useEffect(() => {
    batchNumberRef.current = 0;
    (async () => {
      await completeCurrentRun();
      await loadQuestions();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject, selectedTopic, selectedYear, selectedCategory, subtopic]);

  // Fire pool-exhausted telemetry once per filter when the student has
  // seen everything in the current pool — useful for measuring how often
  // students hit the ceiling.
  const exhaustedSeenRef = useRef<string>('');
  useEffect(() => {
    const filterKey = `${subject}|${selectedTopic}|${selectedYear}`;
    if (
      totalInPool > 0 &&
      unseenRemaining === 0 &&
      exhaustedSeenRef.current !== filterKey
    ) {
      exhaustedSeenRef.current = filterKey;
      track('past_exam.pool_exhausted_seen', {
        subject,
        topic: selectedTopic || null,
        year: selectedYear || null,
        total_in_pool: totalInPool,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unseenRemaining, totalInPool, subject, selectedTopic, selectedYear]);

  // Track time on each question
  useEffect(() => {
    // Start timer when question changes
    setTimeOnQuestion(0);
    timerRef.current = setInterval(() => {
      setTimeOnQuestion(prev => prev + 1);
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [currentQuestionIndex]);

  // Fetch component triggers when explanation is shown
  useEffect(() => {
    if (showExplanation && currentQuestion) {
      fetchResultTriggers();
    } else {
      setResultTriggers([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showExplanation, currentQuestionIndex]);

  const fetchResultTriggers = async () => {
    try {
      const triggers = await aiTutorAPI.getComponentTriggers(
        'real_eslce_question',
        'result_card',
        subject,
        {
          is_correct: isCorrect,
          difficulty: currentQuestion?.metadata.difficulty_level
        }
      );
      setResultTriggers(triggers);
    } catch (err) {
      console.error('Failed to fetch triggers:', err);
    }
  };

  const loadFilterOptions = async () => {
    try {
      const response = await authFetch(`/api/questions/filter-options?subject=${encodeURIComponent(subject)}`);
      const data = await response.json();
      if (data.success) {
        setFilterOptions({
          years: data.years || [],
          topics: data.topics || [],
          categories: data.categories || [],
        });
        // Open on a section rather than on everything. Fifteen topic chips is
        // already a screenful before a student has seen a question, and the
        // verbal and analytical pools will add more. Picking the largest
        // section first means the default view is the one with the most to
        // practise; "All sections" is still one tap away.
        const cats = data.categories || [];
        if (cats.length > 0 && !selectedCategory) {
          // Reopen on the section the student last chose. Someone revising
          // verbal comes back to verbal; without this every visit reset them
          // to whichever section happens to be largest, and they re-tapped the
          // same chip every time. Falls back to the largest section on a first
          // visit, and ignores a remembered section that no longer exists.
          let remembered = '';
          try {
            remembered = localStorage.getItem(SECTION_KEY(subject)) || '';
          } catch {
            /* storage unavailable — fall through to the default */
          }
          const valid = cats.some((c: { name: string }) => c.name === remembered);
          setSelectedCategory(valid ? remembered : cats[0].name);
        }
      }
    } catch (err) {
      console.error('Failed to load filter options:', err);
    }
  };

  const loadQuestions = async (opts?: { extendSessionId?: string }) => {
    try {
      // Only show full loading spinner on initial load (no questions yet)
      if (questions.length === 0) setLoading(true);
      // Load up to 100 per session to match the Telegram bot (past_exams.py).
      // A session is one combo run (consecutive-correct streak); a 50-cap here
      // silently reset streaks at 50 on web while bot users could reach ~100.
      let url = `/api/questions/by-topic?subject=${encodeURIComponent(subject)}&limit=100`;
      if (selectedCategory) url += `&category=${encodeURIComponent(selectedCategory)}`;
      if (selectedTopic) url += `&topic=${encodeURIComponent(selectedTopic)}`;
      if (subtopic) url += `&subtopic=${encodeURIComponent(subtopic)}`;
      if (selectedYear) url += `&year=${selectedYear}`;

      const response = await authFetch(url);
      const data = await response.json();
      const loadedQuestions = data.questions || [];
      const total = data.total_in_pool || loadedQuestions.length;
      const unseen = data.unseen_remaining ?? loadedQuestions.length;
      setTotalInPool(total);
      setUnseenRemaining(unseen);
      setSubjectUnseenRemaining(data.subject_unseen_remaining ?? unseen);
      // Absent for every ESLCE subject, present only for a paid package.
      setAllowance(data.allowance ?? null);
      setLockedCount(data.locked_count || 0);
      // Only meaningful on the unfiltered load; a topic-filtered response
      // narrows it and would wrongly lock everything else.
      if (!selectedTopic && Array.isArray(data.free_topics) && data.free_topics.length) {
        setFreeTopics(data.free_topics);
      }

      // Telemetry — track each batch start so we can measure session-window
      // adoption and pool-exhaustion rates.
      if (loadedQuestions.length > 0) {
        batchNumberRef.current += 1;
        track('past_exam.session.batch_started', {
          subject,
          topic: selectedTopic || null,
          year: selectedYear || null,
          batch_size: loadedQuestions.length,
          total_in_pool: total,
          unseen_remaining: unseen,
          batch_number: batchNumberRef.current,
        });
      }

      // Either extend the current run (keep the same session so the combo
      // continues) or start a fresh session. Done BEFORE rendering questions so
      // sessionId is available immediately when the user clicks "Check Answer".
      if (studentId && loadedQuestions.length > 0) {
        const newIds = loadedQuestions.map((q: ExamQuestion) => q.question_id);
        if (opts?.extendSessionId) {
          // Continuation — append this batch to the live session. Even if the
          // extend call fails, answers still save to the same session_id, so
          // the combo keeps counting; extend only keeps total_questions and the
          // resume snapshot in sync.
          try {
            await pastExamAPI.extendSession(opts.extendSessionId, {
              student_id: studentId,
              question_ids: newIds,
            });
          } catch (err) {
            console.error('Failed to extend session:', err);
          }
        } else if (!skipSessionStart.current) {
          try {
            const sessionData = await pastExamAPI.startSession({
              student_id: studentId,
              subject,
              topic: selectedTopic || undefined,
              year: selectedYear || undefined,
              question_ids: newIds,
            });
            setSessionId(sessionData.session_id);
          } catch (err) {
            console.error('Failed to start session:', err);
          }
        }
      }

      // Now render questions — sessionId is already set
      setQuestions(loadedQuestions);
      // Reset quiz state when questions change — but only if not resuming
      // (handleResume manages its own state)
      if (!skipSessionStart.current) {
        setCurrentQuestionIndex(0);
        setSelectedAnswer(null);
        setShowExplanation(false);
        setExplanationExpanded(false);
        setAnswers(new Map());
        setGridPage(0);
      }
      setError(null);
      skipSessionStart.current = false;
    } catch (err) {
      setError('Failed to load questions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Resume an active session — hydrate the exact question_ids from the
  // session and restore the student's answers on top of them.
  // /questions/by-topic now returns a randomized slice, so we can't assume
  // the currently-loaded `questions` array contains the session's questions.
  const handleResume = async () => {
    if (!activeSessionData) return;
    track('past_exam.resume.clicked', {
      subject,
      session_id: activeSessionData.session_id,
      answered: activeSessionData.responses?.length || 0,
      total: activeSessionData.total_questions,
    });
    try {
      const storedIds: string[] = activeSessionData.question_ids || [];
      let sessionQuestions = questions;
      if (storedIds.length > 0) {
        const data = await pastExamAPI.getQuestionsByIds(storedIds);
        sessionQuestions = data.questions || [];
        setQuestions(sessionQuestions);
      }
      setSessionId(activeSessionData.session_id);
      const restoredAnswers = new Map<string, string>();
      for (const r of activeSessionData.responses || []) {
        restoredAnswers.set(r.question_id, r.answer);
      }
      setAnswers(restoredAnswers);
      const firstUnanswered = sessionQuestions.findIndex(q => !restoredAnswers.has(q.question_id));
      if (firstUnanswered >= 0) {
        setCurrentQuestionIndex(firstUnanswered);
        setGridPage(Math.floor(firstUnanswered / QUESTIONS_PER_PAGE));
      } else {
        setCurrentQuestionIndex(0);
      }
      setResumePrompt(false);
      setActiveSessionData(null);
    } catch (err) {
      console.error('Failed to resume session:', err);
      setError('Failed to resume session');
    }
  };

  // Discard active session and start fresh
  const handleStartFresh = () => {
    track('past_exam.start_fresh.clicked', {
      subject,
      session_id: activeSessionData?.session_id,
    });
    setResumePrompt(false);
    setActiveSessionData(null);
    skipSessionStart.current = false;
    // Trigger a new session by reloading questions
    loadQuestions();
  };

  const currentQuestion = questions[currentQuestionIndex];

  // "Passage · question 2 of 5". Pool passages are shared by up to five
  // questions, and a student meeting the same text repeatedly should be told
  // it is the same one rather than left wondering whether it changed.
  const passageGroup = (() => {
    const text = currentQuestion?.passage;
    if (!text) return null;
    const group = questions.filter((q: any) => q.passage === text);
    if (group.length < 2) return null;
    return {
      index: group.findIndex((q: any) => q.question_id === currentQuestion.question_id) + 1,
      total: group.length,
    };
  })();
  const correctAnswer = currentQuestion?.metadata.correct_answer;
  const isCorrect = selectedAnswer === correctAnswer;
  const hasAnswered = selectedAnswer !== null;
  const progress = questions.length > 0 ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0;
  const score = Array.from(answers.entries()).filter(([qId, ans]) => {
    const q = questions.find(quest => quest.question_id === qId);
    return q && ans === q.metadata.correct_answer;
  }).length;

  // Step 1: Just select the answer (no reveal yet)
  const handleSelectAnswer = (answer: string) => {
    if (showExplanation) return; // Don't allow selection after answer is checked
    setSelectedAnswer(answer);
  };

  // Step 2: Check the answer and reveal result
  const handleCheckAnswer = () => {
    if (!selectedAnswer || showExplanation) return;

    setShowExplanation(true);

    // Track consecutive wrong for AI context
    const answerIsCorrect = selectedAnswer === correctAnswer;
    track('past_exam.check_answer.clicked', {
      question_id: currentQuestion.question_id,
      subject,
      topic: currentQuestion.metadata?.topic,
      is_correct: answerIsCorrect,
      time_spent_s: timeOnQuestion,
    });
    if (answerIsCorrect) {
      setConsecutiveWrong(0);
    } else {
      setConsecutiveWrong(prev => prev + 1);
    }

    const newAnswers = new Map(answers);
    newAnswers.set(currentQuestion.question_id, selectedAnswer);
    setAnswers(newAnswers);

    // Persist answer to backend (fire-and-forget)
    if (studentId && sessionId) {
      pastExamAPI.saveAnswer(sessionId, {
        student_id: studentId,
        question_id: currentQuestion.question_id,
        selected_answer: selectedAnswer,
        is_correct: answerIsCorrect,
        time_spent_seconds: timeOnQuestion,
        topic: currentQuestion.metadata?.topic,
        is_review: currentQuestion.is_review === true
      }).catch(err => console.error('Failed to save answer:', err));

      // Check if all questions answered — complete session
      if (newAnswers.size === questions.length) {
        const completedScore = Array.from(newAnswers.entries()).filter(([qId, ans]) => {
          const q = questions.find(quest => quest.question_id === qId);
          return q && ans === q.metadata.correct_answer;
        }).length;
        track('past_exam.session.batch_completed', {
          subject,
          topic: selectedTopic || null,
          year: selectedYear || null,
          batch_size: questions.length,
          score: completedScore,
          percentage: Math.round((completedScore / questions.length) * 100),
          batch_number: batchNumberRef.current,
          pool_exhausted: unseenRemaining <= questions.length,
        });
        // Don't complete the session here — the run continues if the student
        // taps "Practice another", so the combo can keep growing across batches.
        // The run is completed (and the combo banked) when it actually ends:
        // leaving the quiz or switching filters (see completeCurrentRun).
        // Refresh the stats panel independently so it still updates live.
        pastExamAPI.getStats(studentId, subject)
          .then(s => setStats(s))
          .catch(err => console.error('Failed to refresh stats:', err));
      }
    }
  };

  // Handle trigger click - opens AI panel with the prompt
  const handleTriggerClick = async (trigger: ComponentTrigger) => {
    track('past_exam.trigger.clicked', {
      question_id: currentQuestion?.question_id,
      subject,
      trigger_id: trigger.rule_id,
      trigger_label: trigger.label,
      trigger_category: trigger.category,
    });
    // Track the click
    try {
      await aiTutorAPI.trackTriggerClick(trigger.rule_id);
    } catch (err) {
      console.error('Failed to track trigger:', err);
    }

    // Apply template variable replacement
    let prompt = trigger.prompt;
    if (currentQuestion) {
      const topic = currentQuestion.metadata?.topic || subject;
      const subtopic = currentQuestion.metadata?.subtopic || '';
      // Replace template variables with actual values
      prompt = prompt
        .replace(/\{term\}/g, subtopic || topic)
        .replace(/\{topic\}/g, topic)
        .replace(/\{subtopic\}/g, subtopic);
    }

    // Set the prompt and open AI panel - initialRequest will auto-send it
    setTriggerPrompt(prompt);
    setShowAIHelp(true);
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      const newIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(newIndex);
      setSelectedAnswer(null);
      setShowExplanation(false);
      setExplanationExpanded(false);
      // Update grid page if needed
      setGridPage(Math.floor(newIndex / QUESTIONS_PER_PAGE));
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      track('past_exam.question.previous', {
        subject,
        from_index: currentQuestionIndex,
      });
      const newIndex = currentQuestionIndex - 1;
      setCurrentQuestionIndex(newIndex);
      const prevAnswer = answers.get(questions[newIndex].question_id);
      setSelectedAnswer(prevAnswer || null);
      setShowExplanation(!!prevAnswer);
      setExplanationExpanded(false);
      // Update grid page if needed
      setGridPage(Math.floor(newIndex / QUESTIONS_PER_PAGE));
    }
  };

  const handleRestart = () => {
    track('past_exam.batch.next_clicked', {
      subject,
      topic: selectedTopic || null,
      year: selectedYear || null,
      from_batch: batchNumberRef.current,
      unseen_remaining_before: unseenRemaining,
      pool_exhausted: unseenRemaining <= 0,
    });
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setShowExplanation(false);
    setExplanationExpanded(false);
    setAnswers(new Map());
    // Continue the SAME session so the consecutive-correct combo carries across
    // batches (combo is computed per session_id). Extend the live session
    // rather than starting a new one; only fall back to a fresh session if we
    // somehow have no active session id.
    const liveSessionId = sessionIdRef.current;
    if (liveSessionId) {
      skipSessionStart.current = true;
      // Fetch the next batch — unseen-first selector picks fresh questions.
      loadQuestions({ extendSessionId: liveSessionId });
    } else {
      loadQuestions();
    }
  };

  // Topic exhausted but fresh questions remain elsewhere in the subject — drop
  // the topic filter so the unseen-first selector draws from the whole subject.
  // Clearing selectedTopic triggers the reload effect (no manual loadQuestions).
  const handleExploreOtherTopics = () => {
    track('past_exam.steer_other_topics', {
      subject,
      from_topic: selectedTopic || null,
      subject_unseen_remaining: subjectUnseenRemaining,
    });
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setShowExplanation(false);
    setExplanationExpanded(false);
    setAnswers(new Map());
    // Clear BOTH topic and year so the unseen-first selector draws from the
    // whole subject — matches the promised "fresh in other topics" count
    // (which ignores the year filter) and the bot's pe:gomix behaviour.
    if (selectedYear !== null) setSelectedYear(null);
    setSelectedTopic('');
  };

  const handleQuestionJump = (index: number) => {
    track('past_exam.question.jumped', {
      subject,
      from_index: currentQuestionIndex,
      to_index: index,
    });
    setCurrentQuestionIndex(index);
    const answer = answers.get(questions[index].question_id);
    setSelectedAnswer(answer || null);
    setShowExplanation(!!answer);
    setExplanationExpanded(false);
  };

  // Session state for AI context
  const sessionState = {
    timeOnComponentSeconds: timeOnQuestion,
    lastAnswer: selectedAnswer || undefined,
    lastAnswerCorrect: hasAnswered ? isCorrect : undefined,
    consecutiveWrong: consecutiveWrong,
    questionsAttempted: answers.size,
    questionsCorrect: score
  };

  const allAnswered = !loading && questions.length > 0 && answers.size === questions.length;
  // Student has exhausted the fresh questions in their selected topic, but the
  // subject still has untouched questions in other topics. Drives the honest
  // "you've finished this topic" message + the steer button.
  const topicExhaustedFreshElsewhere =
    !!selectedTopic &&
    questions.length > 0 &&
    unseenRemaining === 0 &&
    subjectUnseenRemaining > 0;

  return (
    <div className="past-esslce-quiz">
      {/* Off-screen share card — real DOM so KaTeX + fonts render.
          Captured by html-to-image when the user taps Share to TikTok. */}
      {currentQuestion && (
        <div
          aria-hidden="true"
          style={{
            position: 'fixed',
            top: 0,
            left: '-99999px',
            pointerEvents: 'none',
            width: '1080px',
            height: '1920px',
          }}
        >
          <ShareCardRender
            ref={shareCardRef}
            questionText={currentQuestion.question_text}
            options={currentQuestion.options.map(o => ({ label: o.label, text: o.text }))}
            subject={currentQuestion.exam?.subject}
            year={currentQuestion.exam?.year_ec || currentQuestion.exam?.year}
            topic={currentQuestion.metadata?.topic}
            url={`${window.location.origin}/q/${currentQuestion.public_slug || currentQuestion.question_id}`}
          />
        </div>
      )}

      {/* CoachCard hidden for launch — resume prompt below already handles active sessions. */}
      {/* <CoachCard
        subject={subject}
        location="past_exams"
        compact
        onAction={(action: CoachAction) => {
          if (action.type === 'practice_topic' && action.payload) {
            setSelectedTopic(action.payload);
          } else if (action.type === 'review_mistakes' || action.type === 'start_past_exams') {
            setSelectedTopic(action.payload || '');
          }
        }}
      /> */}

      {/* Compact Stats — inline, one line. Hide accuracy until 5+ questions (not meaningful before that). */}
      {stats && stats.total_questions_practiced > 0 && (
        <div className="exam-stats-compact">
          <span>{stats.total_questions_practiced} solved</span>
          {stats.total_questions_practiced >= 5 && (
            <>
              <span className="stats-sep">·</span>
              <span className={
                stats.accuracy_percent >= 70 ? 'stats-good'
                : stats.accuracy_percent >= 50 ? 'stats-warn'
                : 'stats-bad'
              }>
                {Math.round(stats.accuracy_percent)}% accuracy
              </span>
            </>
          )}
          {stats.suggested_topic && (
            <>
              <span className="stats-sep">·</span>
              <span className="stats-suggest" onClick={() => setSelectedTopic(stats.suggested_topic!)}>
                Try {stats.suggested_topic} →
              </span>
            </>
          )}
        </div>
      )}

      {/* Resume Prompt */}
      {resumePrompt && activeSessionData && (
        <div className="resume-prompt">
          <div className="resume-info">
            <span className="resume-icon">📝</span>
            <p>
              You left off at question {(activeSessionData.responses?.length || 0) + 1} of {activeSessionData.total_questions}
              {activeSessionData.hoursOld > 24 && <span className="stale-hint"> ({Math.floor(activeSessionData.hoursOld / 24)}d ago)</span>}
            </p>
          </div>
          <div className="resume-actions">
            <button className="resume-btn primary" onClick={handleResume}>Resume</button>
            <button className="resume-btn secondary" onClick={handleStartFresh}>Start Fresh</button>
          </div>
        </div>
      )}

      {/* Section chips. Rendered ABOVE topics because it is the coarser cut —
          a student narrows by section, then by topic. Absent entirely for
          subjects with no sections, so ESSLCE is unchanged. */}
      {/* One section at a time — the same control the Lessons tab uses. With
          fifteen topics and more coming, an unfiltered chip row is six rows
          deep on a phone before the student sees a question. There is no "All
          sections": the point is to show one group, and the topic row inside
          it already has an "All topics". */}
      {/* The Questions tab never said the student was on a trial. The Lessons
          tab has said so all along, and the two are the same package — so a
          student could browse here, meet only twenty questions, and read the
          whole thing as a small product rather than a locked one. */}
      {allowance && allowance.access !== 'full' && (
        <div className={`exam-trial-strip${trialSpent ? ' exam-trial-strip--spent' : ''}`}>
          <span className="exam-trial-strip__text">
            {trialSpent ? (
              <>
                {/* Deliberately not `lockedCount`: that is scoped to the
                    filter the student happens to be on, so the wall cited
                    "354 more" while standing in the Quantitative section. The
                    wall is about the whole package. */}
                <strong>Your free questions are used up.</strong> The full
                package opens every question, all 38 lessons and the mock exam.
              </>
            ) : (
              <>
                {/* The number a student should never have to infer. It counts
                    DOWN, because what matters is what is left. */}
                <strong>
                  {allowance.remaining ?? allowance.limit ?? 20} of{' '}
                  {allowance.limit ?? 20} free questions left
                </strong>
                {' '}— across the whole paper, not per topic.
              </>
            )}
          </span>
          <button
            className="exam-trial-strip__cta"
            onClick={() => {
              track('past_exam.trial_strip.unlock', { subject, spent: trialSpent });
              window.location.href = '/packages/uat-aau/checkout';
            }}
          >
            Get full access — 400 birr
          </button>
        </div>
      )}

      {(filterOptions.categories?.length || 0) > 0 && (
        <div className="exam-topic-chips exam-category-chips">
          {filterOptions.categories!.map((c) => (
            <button
              key={c.name}
              className={`exam-topic-chip ${selectedCategory === c.name ? 'active' : ''}`}
              disabled={trialSpent}
              onClick={() => {
                // Spent trials do not get to keep shopping through filters.
                if (trialSpent) return;
                // Selecting the active section is a no-op rather than a
                // toggle back to everything: with no "All sections" button,
                // toggling off would leave the row with nothing selected.
                if (c.name === selectedCategory) return;
                track('past_exam.filter_category.changed', { subject, category: c.name });
                rememberSection(subject, c.name);
                setSelectedCategory(c.name);
              }}
            >
              {c.name.replace(/ Reasoning$/, '')}
              <span className="chip-count">{c.count}</span>
            </button>
          ))}
        </div>
      )}

      {/* Topic chips — like lessons tab */}
      {filterOptions.topics.length > 0 && (
        <div className="exam-topic-chips">
          <button
            className={`exam-topic-chip ${!selectedTopic ? 'active' : ''}`}
            disabled={onTrial}
            onClick={() => {
              if (onTrial) return;
              track('past_exam.filter_topic.changed', { subject, topic: null });
              setSelectedTopic('');
            }}
          >
            All
          </button>
          {filterOptions.topics
            .filter(
              (t) => !selectedCategory || !t.section || t.section === selectedCategory
            )
            .map((topic) => (
            <button
              key={topic.name}
              /* Dimmed and padlocked when the free tier holds nothing in it —
                 the same answer the lessons tab gives, instead of twenty-seven
                 chips that all look equally available. */
              className={`exam-topic-chip ${selectedTopic === topic.name ? 'active' : ''}${
                freeTopics && !freeTopics.includes(topic.name)
                  ? ' exam-topic-chip--locked'
                  : ''
              }`}
              disabled={trialSpent}
              onClick={() => {
                if (trialSpent) return;
                track('past_exam.filter_topic.changed', { subject, topic: topic.name });
                setSelectedTopic(topic.name);
              }}
            >
              {freeTopics && !freeTopics.includes(topic.name) && (
                <span aria-hidden="true">🔒 </span>
              )}
              {/* The server's short name when it has one, otherwise the
                  existing per-topic override table. Both, rather than either,
                  because the override table already carries ESLCE decisions
                  that should not be undone by a generic shortener. */}
              {topic.short || displayTopic(topic.name)}
              {topic.pct > 0 && <span className="chip-count">{topic.pct}%</span>}
            </button>
          ))}
        </div>
      )}

      {/* Year chips + progress bar.
          UAT has no years. Its items are authored practice, and `year_gc` is
          being used as an arbitrary container id — 2036, 2047 — so a year chip
          there offers a student a filter on a serial number that looks like a
          date. ESSLCE questions DO come from a dated paper, where the filter is
          the whole point. */}
      <div className="exam-subfilter-row">
        {/* Gated on whether there ARE years, not on which package this is.
            The old test named UAT, so every package added after it inherited
            a year filter over container ids — EAU shipped with 40 chips
            reading 2101-2308. The server now returns years only for dated
            papers, so an empty list is the honest signal. */}
        {filterOptions.years.length > 0 && (
        <div className="year-chips">
          <button
            className={`year-chip ${!selectedYear ? 'active' : ''}`}
            onClick={() => {
              track('past_exam.filter_year.changed', { subject, year: null });
              setSelectedYear(null);
            }}
          >
            All Years
          </button>
          {filterOptions.years.map((year) => (
            <button
              key={year}
              className={`year-chip ${selectedYear === year ? 'active' : ''}`}
              onClick={() => {
                track('past_exam.filter_year.changed', { subject, year });
                setSelectedYear(year);
              }}
            >
              '{String(year).slice(-2)}
            </button>
          ))}
        </div>
        )}
        <div className="exam-progress-info">
          <span className="exam-q-position">
            Q{currentQuestionIndex + 1}/{questions.length}
          </span>
          {totalInPool > questions.length && (
            <span className="exam-pool-badge" title={`${totalInPool} questions available in ${subject}`}>
              of {totalInPool}
            </span>
          )}
          {/* The rest of the package, named while they browse rather than
              discovered when a filter comes back empty. */}
          {lockedCount > 0 && allowance && allowance.access !== 'full' && (
            <span
              className="exam-locked-badge"
              title={`${lockedCount} more questions come with the full package`}
            >
              🔒 {lockedCount} more
            </span>
          )}
          {answers.size > 0 && (
            <span className="exam-score-badge">
              {score}/{answers.size} ✓
            </span>
          )}
        </div>
      </div>

      {/* Thin progress track */}
      <div className="exam-progress-track">
        <div
          className="exam-progress-fill"
          style={{ width: `${questions.length > 0 ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0}%` }}
        />
      </div>

      {/* Loading / Empty / Question states */}
      {loading ? (
        <div className="exam-loading-inline">
          <span className="loading-spinner-sm">⏳</span>
          <span>Loading questions...</span>
        </div>
      ) : error || questions.length === 0 ? (
        <div className="exam-empty-inline">
          <span>📚</span>
          {/* On the free tier an empty topic is not a missing topic — it is a
              topic outside the twenty free questions. Saying "no questions
              found" there reads as broken content for something we are
              selling, which is the opposite of what a preview is for. */}
          <span>
            {error ||
              (allowance && allowance.access !== 'full' && lockedCount > 0
                ? `${lockedCount} question${lockedCount === 1 ? '' : 's'} here — they come with the full package.`
                : allowance && allowance.access !== 'full'
                ? `Your ${allowance.limit ?? 20} free questions don't cover this topic. Try another.`
                : 'No questions found for this filter. Try a different topic or year.')}
          </span>
          {allowance && allowance.access !== 'full' && lockedCount > 0 && (
            <button
              className="exam-unlock-cta"
              onClick={() => {
                track('past_exam.locked_topic.unlock', { subject, topic: selectedTopic || null });
                window.location.href = `/packages/${encodeURIComponent('uat-aau')}/checkout`;
              }}
            >
              Get full access — 400 birr
            </button>
          )}
        </div>
      ) : (
      <>
      {/* Question Card */}
      <Reportable
        entityType="question"
        entityId={currentQuestion.question_id}
        subTargets={['stem', 'option_A', 'option_B', 'option_C', 'option_D', 'image', 'topic']}
      >
      <div className="question-card-esslce">
        {/* Question Header */}
        <div className="question-header-esslce">
          {(() => {
            if (sourceLabel === null) return null;
            if (sourceLabel) return <span className="eslce-badge">{sourceLabel}</span>;
            // No caller-supplied label: fall back to the ESLCE year, but ONLY
            // when there is a real one. A pool is undated and stores year 0,
            // which this used to render as "ESLCE '0 EC" — a claim of
            // provenance the item does not have, which is the one thing this
            // badge exists to be careful about.
            const yr = currentQuestion.exam.year_ec || currentQuestion.exam.year;
            if (!yr) return null;
            return (
              <span className="eslce-badge">{`ESLCE '${String(yr).slice(-2)} EC`}</span>
            );
          })()}
          <span className="question-number">Q{currentQuestion.question_number}</span>
          {currentQuestion.is_review && (
            <span
              style={{
                marginLeft: 'auto',
                fontSize: 12,
                fontWeight: 600,
                color: '#b45309',
                background: '#fff7ed',
                border: '1px solid #fed7aa',
                borderRadius: 999,
                padding: '2px 8px',
              }}
            >
              🔁 You missed this before
            </span>
          )}
        </div>

        {/* The chart an authored item asks about, in the same slot a past
            paper's scanned diagram occupies. Same component the lesson player
            uses, so theme-safety and mobile sizing come for free. */}
        {currentQuestion.figure && (
          <ContentFigure figure={currentQuestion.figure} />
        )}

        {/* Media Display (images, diagrams, maps) */}
        {currentQuestion.media && currentQuestion.media.length > 0 && (
          <MediaDisplay
            media={currentQuestion.media}
            showCaption={true}
            maxHeight={280}
            allowZoom={true}
          />
        )}

        {/* The passage, above the stem and visibly NOT the stem. 388 of the
            UAT pool questions carry one; until the API sent it the student
            read "What is the main conclusion?" with no argument in front of
            them. */}
        <PassagePanel
          text={currentQuestion.passage}
          cacheKey={`pool:${currentQuestion.question_id}`}
          surface="past_exam_question"
          position={passageGroup}
          trackProps={{ subject, question_id: currentQuestion.question_id }}
        />

        {/* Question Text */}
        <div className="question-text-esslce">
          <MarkdownRenderer inline>{currentQuestion.question_text}</MarkdownRenderer>
        </div>

        {/* Options */}
        <div className="options-esslce">
          {currentQuestion.options.map((option) => {
            const isSelected = selectedAnswer === option.label;
            const isCorrectOption = option.label === correctAnswer;
            const isChecked = showExplanation; // Answer has been checked (submitted)

            let optionClass = 'option-esslce';
            if (isChecked) {
              // After Check Answer: show correct/incorrect styling
              if (isSelected && isCorrect) {
                optionClass += ' correct';
              } else if (isSelected && !isCorrect) {
                optionClass += ' incorrect';
              } else if (isCorrectOption) {
                optionClass += ' correct-answer';
              }
            } else if (isSelected) {
              // Before Check Answer: just show selected styling
              optionClass += ' selected';
            }

            return (
              <button
                key={option.label}
                className={optionClass}
                onClick={() => handleSelectAnswer(option.label)}
                disabled={isChecked}
              >
                <span className="option-label-esslce">{option.label}</span>
                <span className="option-text-esslce"><MarkdownRenderer inline>{option.text}</MarkdownRenderer></span>
                {isChecked && isCorrectOption && <span className="check-icon">✓</span>}
                {isChecked && isSelected && !isCorrect && <span className="x-icon">✗</span>}
              </button>
            );
          })}
        </div>

        {/* Check Answer Button - appears when answer selected but not checked */}
        {selectedAnswer && !showExplanation && (
          <button className="check-answer-btn" onClick={handleCheckAnswer}>
            Check Answer
          </button>
        )}

        {/* Explanation */}
        {showExplanation && (
          <div className={`explanation-box ${isCorrect ? 'correct' : 'incorrect'}`}>
            {/* Compact header - always visible */}
            <div className="explanation-header">
              {isCorrect ? (
                <>
                  <span className="icon">✅</span>
                  <span className="text">Correct!</span>
                </>
              ) : (
                <>
                  <span className="icon">❌</span>
                  <span className="text">Incorrect — Answer: {correctAnswer}</span>
                </>
              )}
            </div>

            {/* Show Explanation toggle */}
            {currentQuestion.metadata.explanation && (
              <button
                className="show-explanation-toggle"
                onClick={() => {
                  const next = !explanationExpanded;
                  setExplanationExpanded(next);
                  // Only count the open, not the close — we want to know
                  // whether students engage with the explanation, not how
                  // many times they toggle it.
                  if (next) {
                    track('past_exam.explanation.shown', {
                      question_id: currentQuestion.question_id,
                      topic: currentQuestion.metadata?.topic,
                      is_correct: isCorrect,
                    });
                  }
                }}
              >
                {explanationExpanded ? '▼ Hide Explanation' : '▶ Show Explanation'}
              </button>
            )}

            {/* Expanded explanation content */}
            {explanationExpanded && (
              <div className="explanation-content">
                {!isCorrect && (
                  <div className="correct-answer-info">
                    <strong>Correct Answer:</strong> {correctAnswer}. <MarkdownRenderer inline>{currentQuestion.options.find(opt => opt.label === correctAnswer)?.text || ''}</MarkdownRenderer>
                  </div>
                )}

                {currentQuestion.metadata.explanation && (
                  <Reportable
                    entityType="explanation"
                    entityId={currentQuestion.question_id}
                    subTargets={['reasoning', 'wrong_answer_key', 'misleading', 'typo']}
                  >
                    <div className="explanation-text">
                      <MarkdownRenderer>{currentQuestion.metadata.explanation}</MarkdownRenderer>
                    </div>
                    <TranslateAmharicButton
                      surface="past_exam_explanation"
                      cacheKey={currentQuestion.question_id}
                      text={currentQuestion.metadata.explanation}
                      trackProps={{
                        question_id: currentQuestion.question_id,
                        subject: currentQuestion.exam?.subject,
                        topic: currentQuestion.metadata?.topic,
                      }}
                    />
                  </Reportable>
                )}

                <div className="topic-info">
                  📍 {currentQuestion.metadata.topic} → {currentQuestion.metadata.subtopic}
                </div>
              </div>
            )}

            {/* Follow-up trigger buttons - always visible */}
            {resultTriggers.length > 0 && (
              <div className="result-triggers">
                <div className="trigger-chips">
                  {resultTriggers.map((trigger) => (
                    <button
                      key={trigger.rule_id}
                      className={`trigger-chip ${trigger.category}`}
                      onClick={() => handleTriggerClick(trigger)}
                    >
                      {trigger.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

          </div>
        )}

      </div>
      </Reportable>

      {/* Navigation — primary post-answer action. Sits right after the
          explanation so the student's next-step decision is one tap away. */}
      <div className="quiz-navigation">
        <button
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0}
          className="nav-btn secondary"
        >
          ← Previous
        </button>

        {/* A spent trial gets ONE forward action, and it is the offer. Leaving
            "Next", "Skip", "Review again" and "Explore other topics" alive
            after the twentieth question is what made the wall feel optional:
            every one of them is a way to keep going. */}
        {trialSpent ? (
          <button
            className="nav-btn primary"
            onClick={() => {
              track('past_exam.spent.unlock', { subject });
              window.location.href = '/packages/uat-aau/checkout';
            }}
          >
            🔓 Get full access — 400 birr
          </button>
        ) : allAnswered ? (
          topicExhaustedFreshElsewhere ? (
            <button onClick={handleExploreOtherTopics} className="nav-btn primary">
              ✨ Explore other topics →
            </button>
          ) : (
            <button onClick={handleRestart} className="nav-btn primary">
              {unseenRemaining > 0
                ? `Practice another ${Math.min(questions.length, unseenRemaining)} →`
                : totalInPool > questions.length
                  ? '🔄 Review again'
                  : '🔄 Restart Practice'}
            </button>
          )
        ) : currentQuestionIndex < questions.length - 1 ? (
          <button
            onClick={handleNext}
            className={`nav-btn ${hasAnswered ? 'primary' : 'skip'}`}
          >
            {hasAnswered ? 'Next →' : 'Skip →'}
          </button>
        ) : (
          <button
            onClick={handleRestart}
            className={`nav-btn ${hasAnswered ? 'primary' : 'skip'}`}
          >
            {hasAnswered ? 'Finish' : 'Skip & Finish'}
          </button>
        )}
      </div>

      {/* AI Help Panel comes BEFORE the share row so the floating Ask
          Lomi button is the first post-nav action a student sees. Share
          is celebratory / secondary — it sits last. */}
      <AIHelpPanel
        context="esslce"
        source="web_past_exam"
        questionId={currentQuestion?.question_id}
        questionText={currentQuestion?.question_text}
        sessionState={sessionState}
        isOpen={showAIHelp}
        onToggle={() => setShowAIHelp(!showAIHelp)}
        initialRequest={triggerPrompt ? { question: triggerPrompt } : undefined}
        onInitialRequestSent={() => setTriggerPrompt(null)}
      />

      {/* Share row — compact, icon-only on every viewport. Was eating a
          full row of mobile real estate for three labelled buttons. The
          icons + tooltips/aria-labels keep accessibility intact. Only
          shown after the student has answered. */}
      {showExplanation && currentQuestion && (
        <div className="result-share result-share-below-nav result-share-compact">
          <ShareButton
            url={`/q/${currentQuestion.public_slug || currentQuestion.question_id}`}
            title={`${sourceLabel || 'ESSLCE'} practice · ${currentQuestion.exam?.subject || ''}`}
            text={`Try this ${currentQuestion.exam?.subject || 'ESSLCE'} question on Lomi`}
            className="result-share-btn"
            label="Share link"
            iconOnly
          />
          <CopyLinkButton
            url={`/q/${currentQuestion.public_slug || currentQuestion.question_id}`}
            className="result-share-btn"
            label="Copy link"
            iconOnly
          />
          <button
            type="button"
            className="result-share-btn result-share-btn-tiktok"
            aria-label="Share card to TikTok or Instagram"
            title="Share to TikTok / Instagram"
            onClick={async () => {
              track('past_exam.share.tiktok_clicked', {
                question_id: currentQuestion.question_id,
                subject: currentQuestion.exam?.subject,
              });
              if (!shareCardRef.current) return;
              try {
                const bundle = await captureShareCardNode(
                  shareCardRef.current,
                  `lomi-${(currentQuestion.exam?.subject || 'question').toLowerCase()}.png`,
                );
                const input = {
                  questionText: currentQuestion.question_text,
                  subject: currentQuestion.exam?.subject,
                  year: currentQuestion.exam?.year_ec || currentQuestion.exam?.year,
                  topic: currentQuestion.metadata?.topic,
                  url: `${window.location.origin}/q/${currentQuestion.public_slug || currentQuestion.question_id}`,
                };
                if (bundle.canNativeShare) {
                  const outcome = await nativeShareBundle(bundle, input);
                  if (outcome === 'unsupported') downloadBundle(bundle);
                } else {
                  downloadBundle(bundle);
                }
                setTimeout(() => URL.revokeObjectURL(bundle.objectUrl), 1500);
              } catch (err) {
                console.warn('Share card failed', err);
              }
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden style={{ color: '#25f4ee', filter: 'drop-shadow(1px -1px 0 #fe2c55)' }}>
              <path fill="currentColor" d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.298-.002.595.042.88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z"/>
            </svg>
          </button>
        </div>
      )}

      {/* Final Score */}
      {allAnswered && (
        <div className="final-score">
          <h3>{totalInPool > questions.length ? 'Set complete! 🎉' : 'All Done! 🎉'}</h3>
          <div className="score-display">
            <span className="score-num">{score}</span>
            <span className="score-total">/ {questions.length}</span>
          </div>
          <p className="score-percentage">
            {Math.round((score / questions.length) * 100)}% Correct
          </p>
          {unseenRemaining > 0 ? (
            <p className="score-pool-hint">
              {unseenRemaining} more {unseenRemaining === 1 ? 'question' : 'questions'} available
              {selectedTopic ? ` in ${displayTopic(selectedTopic)}` : ` in ${subject}`}
            </p>
          ) : topicExhaustedFreshElsewhere ? (
            <p className="score-pool-hint">
              ✨ You've answered every {displayTopic(selectedTopic)} question —{' '}
              {subjectUnseenRemaining} fresh waiting in other {subject} topics.
            </p>
          ) : totalInPool > questions.length ? (
            <p className="score-pool-hint">
              You've seen every {subject} question — restart to review.
            </p>
          ) : null}
        </div>
      )}
      {/* Earned-moment feedback — only on a non-low result (Signal Engine) */}
      {allAnswered && score >= questions.length * 0.6 && (
        <FeedbackPrompt surface="web_assessment_done" trigger="past_exam_done" />
      )}
      </>
      )}
    </div>
  );
};

export default PastESSLCEQuiz;
